import {ApDynformsConfigFieldset}                   from '../../../ap-dynforms/config/ap-dynforms-config-fieldset';
import {ApDynformsConfigComboBox}                   from '../../../ap-dynforms/config/ap-dynforms-config-combobox';
import {
  ApDynGridPropertyColumnConfig
}                                                   from '../../../ap-dyngrids/config/ap-dyn-grid-property-column-config';
import {
  ApDynformsConfigNumerictextbox
}                                                   from '../../../ap-dynforms/config/ap-dynforms-config-numerictextbox';
import {delay, distinctUntilChanged, filter, map}   from 'rxjs/operators';
import {ApDynformsValidator}                        from '../../../ap-dynforms/ap-dynforms-validator';
import {Validators}                                 from '@angular/forms';
import {BehaviorSubject, combineLatest, Observable} from 'rxjs';
import {ApDynGridGroupColumnConfig}                 from '../../../ap-dyngrids/config/ap-dyn-grid-group-column-config';
import {ApDynGridColumnConfigBase}                  from '../../../ap-dyngrids/config/ap-dyn-grid-column-config-base';
import {
  ApNFertilizationComponentService
}                                                   from '../../../n-fertilization/services/ap-n-fertilization-component.service';
import {GetRoundNumericPipe}                        from '../../../ap-utils';
import {Injectable}                                 from '@angular/core';
import {ApTranslationService}                       from '../../../ap-utils/service/ap-translation.service';
import {NFertilizationCommonEntryService}           from '../services/n-fertilization-common-entry.service';

@Injectable()
export class RapeFertilizationConfig {
  private _minAllowed = 0;
  private _maxAllowed = 250;
  private _ecMin$ = new BehaviorSubject<number | undefined>(undefined);
  private _ecMax$ = new BehaviorSubject<number | undefined>(undefined);

  constructor(private translationService: ApTranslationService,
              private nFertilizationComponentService: ApNFertilizationComponentService,
              private nFertilizationCommonEntryService: NFertilizationCommonEntryService,
              private roundNumericPipe: GetRoundNumericPipe) {
  }

  public formBuilderConfig(values: any, formValues$: Observable<any>, cropKey: number | undefined,
                           isModalField: boolean): ApDynformsConfigFieldset[] {
    this._ecMin$.next(undefined);
    this._ecMax$.next(undefined);
    const min$ = formValues$.pipe(
      filter(x => x['Minimum'] != null && x['Minimum'] >= 0),
      map((v) => v['Minimum'])
    );
    const max$ = formValues$.pipe(
      filter(x => x['Maximum'] != null && x['Maximum'] >= 0),
      map((v) => v['Maximum'])
    );
    return [
      new ApDynformsConfigFieldset({
        key: 'Agronomy',
        legend: 'Global__Agronomy',
        useMaxWidth: isModalField,
        config: [
          new ApDynformsConfigComboBox({
            key: 'Appl',
            label: 'Global__N_ApplNum',
            value: values['Appl'],
            value$: formValues$.pipe(
              filter(formValues => formValues && Object.keys(formValues).length > 0),
              distinctUntilChanged((p, n) => p['Appl'] === n['Appl']),
              filter((formValues) => formValues['Appl'] != null),
              map((formValues) => {
                const newMin = +this.nFertilizationComponentService.getApplicationMinMax(311, formValues['Appl']).min;
                const max = +this.nFertilizationComponentService.getApplicationMinMax(311, formValues['Appl']).max;
                // ensure that min is always smaller than max
                if (newMin > this._ecMax$.value) {
                  // set max first
                  this._ecMax$.next(max);
                  this._ecMin$.next(newMin);
                } else { // newMax < this._min$.value)
                  // set min first
                  this._ecMin$.next(newMin);
                  this._ecMax$.next(max);
                }
                return formValues['Appl'];
              }),
            ),
            options: [
              {value: 1, text: 'AgriConnect_ApplNum_1'},
              {value: 2, text: 'AgriConnect_ApplNum_2'}
            ],
            valueField: 'value',
            textField: 'text',
            translate: true,
            validators: [
              new ApDynformsValidator({validator: Validators.required, errorKey: 'Settings__Msg_Vali_Value_Required'}),
            ]
          }),
          new ApDynformsConfigNumerictextbox({
            key: 'ECStage',
            label: 'Global__ECStage',
            format: 'n0',
            value: values['ECStage'],
            value$: combineLatest([formValues$, this._ecMin$, this._ecMax$]).pipe(
              filter(([, min, max]) => min != null && max != null),
              map(([formValues]) => formValues),
              distinctUntilChanged((p, n) => p['Appl'] === n['Appl']),
              filter((v) => v['Appl'] != null),
              delay(10),
              map((formValues) => {
                if (formValues['ECStage'] == null ||
                  formValues['ECStage'] < this._ecMin$.getValue() ||
                  formValues['ECStage'] > this._ecMax$.getValue()) {
                  return this._ecMin$.getValue();
                }
                return formValues['ECStage'];
              })
            ),
            min: this._ecMin$,
            max: this._ecMax$,
            step: 1,
            validators: [
              new ApDynformsValidator({validator: Validators.required, errorKey: 'Settings__Msg_Vali_Value_Required'}),
            ]
          }),
          new ApDynformsConfigNumerictextbox({
            key: 'Threshold',
            label: 'Global__Threshold_SN',
            format: 'n0',
            value: values['Threshold'],
            value$: formValues$.pipe(
              filter(formValues => formValues['ECStage'] != null),
              distinctUntilChanged((prev, curr) => prev['ECStage'] === curr['ECStage']),
              map(formValues => this.nFertilizationCommonEntryService.getThreshold(formValues['ECStage'], cropKey))
            ),
            min: 0,
            max: 100,
            validators: [
              new ApDynformsValidator({validator: Validators.required, errorKey: 'Settings__Msg_Vali_Value_Required'}),
            ]
          }),
          new ApDynformsConfigComboBox({
            key: 'YieldExpectation',
            label: 'Docu_Ini__YieldExpectation',
            value: values['YieldExpectation'] ?? 4,
            options: formValues$.pipe(
              distinctUntilChanged((prev, curr) => prev['Appl'] === curr['Appl']),
              map(formValues => this._getYieldExpectationOptions(formValues['Appl']))
            ),
            infoText: 'Global_Yield_Expectation_Text_Info',
            textField: 'text',
            valueField: 'value',
            validators: [
              new ApDynformsValidator({validator: Validators.required, errorKey: 'Settings__Msg_Vali_Value_Required'}),
            ]
          }),
          new ApDynformsConfigComboBox({
            key: 'DeadBioMass',
            label: 'Global__ExtinctBiomass',
            value: values['DeadBioMass'] ?? 0,
            options: [
              {value: 0, text: '0 %'},
              {value: 10, text: '10 %'},
              {value: 20, text: '20 %'},
              {value: 30, text: '30 %'},
              {value: 40, text: '40 %'},
            ],
            disabled$: formValues$.pipe(
              distinctUntilChanged((p, n) => p['Appl'] === n['Appl']),
              map((v) => v['Appl'] !== 1)
            ),
            textField: 'text',
            valueField: 'value',
            validators: [
              new ApDynformsValidator({validator: Validators.required, errorKey: 'Settings__Msg_Vali_Value_Required'}),
            ]
          }),
          new ApDynformsConfigComboBox({
            key: 'Potential',
            label: 'Global__N_MineralPot',
            value: values['Potential'] ?? 1,
            disabled$: formValues$.pipe(
              distinctUntilChanged((p, n) => p['Appl'] === n['Appl']),
              map((v) => v['Appl'] !== 2)
            ),
            textField: 'text',
            valueField: 'value',
            translate: true,
            options: [
              {value: 0, text: 'AgriConnect_MineralPot_Low'},
              {value: 1, text: 'AgriConnect_MineralPot_Medium'},
              {value: 2, text: 'AgriConnect_MineralPot_High'}
            ]
          })
        ]
      }),
      new ApDynformsConfigFieldset({
        legend: 'Nutrients_Pages_Popups__Amount',
        key: 'Amount',
        useMaxWidth: isModalField,
        config: [
          new ApDynformsConfigNumerictextbox({
            key: 'Minimum',
            label: 'Nutrients__Minimum_kg_n_per_ha',
            infoText: 'RapeFertilizationMinimumAdjustmentInfoText',
            value: values['Minimum'],
            value$: min$,
            min: this._minAllowed,
            max: max$,
            step: 1,
            format: 'n0',
            validators: [
              new ApDynformsValidator({validator: Validators.required, errorKey: 'Settings__Msg_Vali_Value_Required'}),
            ]
          }),
          new ApDynformsConfigNumerictextbox({
            key: 'Maximum',
            label: 'Nutrients__Maximum_kg_n_per_ha',
            infoText: 'RapeFertilizationMaximumAdjustmentInfoText',
            value: values['Maximum'],
            value$: max$,
            min: min$,
            max: this._maxAllowed,
            step: 1,
            format: 'n0',
            validators: [
              new ApDynformsValidator({validator: Validators.required, errorKey: 'Settings__Msg_Vali_Value_Required'}),
            ]
          }),
          new ApDynformsConfigNumerictextbox({
            key: 'Constant',
            label: 'Nutrients__Constant_Amount_kg_N_per_ha',
            value: values['Constant'],
            min: min$,
            max: max$,
            step: 1,
            format: 'n0',
            validators: [
              new ApDynformsValidator({validator: Validators.required, errorKey: 'Settings__Msg_Vali_Value_Required'}),
            ]
          })
        ],
      })
    ];
  }

  public gridBuilderConfig(): ApDynGridColumnConfigBase[] {
    return [
      new ApDynGridPropertyColumnConfig({
        title: 'Global__Control_Function',
        field: 'ControlFactor',
        hide: true,
        width: 50
      }),
      new ApDynGridGroupColumnConfig({
        title: 'Global__KgNPerHa',
        groupColumns: [
          new ApDynGridPropertyColumnConfig({
            title: 'AgriConnect_View__Reference',
            field: 'Reference',
            width: 50
          }),
          new ApDynGridPropertyColumnConfig({
            title: 'Global__Threshold',
            field: 'Threshold',
            hide: true,
            width: 50
          }),
          new ApDynGridPropertyColumnConfig({
            title: 'Global__RateConst',
            field: 'Constant',
            hide: true,
            width: 50,
            pipes: [{
              pipe: this.roundNumericPipe,
              args: [0]
            }]
          }),
          new ApDynGridPropertyColumnConfig({
            title: 'Global__Min',
            field: 'Minimum',
            width: 50,
            pipes: [{
              pipe: this.roundNumericPipe,
              args: [0]
            }]
          }),
          new ApDynGridPropertyColumnConfig({
            title: 'Global__Max',
            field: 'Maximum',
            width: 50,
            pipes: [{
              pipe: this.roundNumericPipe,
              args: [0]
            }]
          })
        ]
      })
    ];
  }

  private _getYieldExpectationOptions(applicationRate: number | undefined): { value: number, text: string }[] {
    const value3Text = this.translationService.translate('Osr_YieldExpectation_Value3');
    const value4Text = this.translationService.translate('Osr_YieldExpectation_Value4');
    const value6Text = this.translationService.translate('Osr_YieldExpectation_Value6');
    if (applicationRate === 2) {
      return [
        {value: 3, text: `${value3Text} (${this.translationService.translate('Osr_YieldExpectation_Value3_2ndRate')})`},
        {value: 4, text: `${value4Text} (${this.translationService.translate('Osr_YieldExpectation_Value4_2ndRate')})`},
        {value: 6, text: `${value6Text} (${this.translationService.translate('Osr_YieldExpectation_Value6_2ndRate')})`}
      ];
    }
    return [
      {value: 3, text: value3Text},
      {value: 4, text: value4Text},
      {value: 6, text: value6Text}
    ];
  }
}
